import { useState } from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"

import AddClientModal from "./AddModal"
import Button from "ui/buttons/Button"
import { Clients } from "models/clientgroups"
import { ReactComponent as ClientsIcon } from "assets/icon-client.svg"
import { EmptyState } from "components/EmptyState"
import { SnackBar } from "components/SnackBar"
import { Table } from "components/tables"
import { WithAuthority } from "components/WithAuthority"
import { columns } from "./columns"
import { fetchClients } from "api/clients"
import { limitAndOffset } from "api/utils"
import { patchAssignedClient } from "api/clientgroups"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useLocationSearch } from "hooks/useLocationSearch"
import { useParams } from "react-router-dom"
import { useToggle } from "hooks/useToggle"

const styles = {
  btn: css`
    text-transform: none;
    max-height: 30px;
    min-width: 102px;
    margin-top: 1px;
  `,
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
}

function ClientgroupClients() {
  const { t } = useI18n()
  const { id } = useParams<{ id: string }>()
  const { open, onOpen, onClose } = useToggle()
  const [showSuccess, setShowSuccess] = useState<string>()
  const [modalInfo, setModalInfo] = useState<ModalInfo>(undefined)
  const { hasAuthority } = useAuthenticated()
  const ls = useLocationSearch()
  const params = ls.map(limitAndOffset)
  params.append("groupId", id)

  const [deleteClient] = useMutation(patchAssignedClient, {
    onSuccess: () => {
      setModalInfo(undefined)
      setShowSuccess(t("client.delete.successfully"))
      queryCache.invalidateQueries("clients-list")
    },
  })
  const q = useQuery(["clients-list", params.toString()], fetchClients)
  const { status, data } = q
  const clients = data?.data

  const openDeleteModal = (clientIds: string) => {
    setModalInfo({
      type: "primary_destructive",
      submitLabel: t("modal.label.delete"),
      cancelLabel: t("modal.label.cancel"),
      onSubmit: () => {
        if (id)
          deleteClient({
            id: id,
            clientIds: [clientIds],
          })
      },

      title: t("client.modal.delete.title"),
      description: t("client.modal.delete.description"),
    })
  }

  const total = clients?.length
  const existingClientIds = clients?.map((x) => x.id) ?? []

  return (
    <>
      <Table<Clients>
        id="groupsClients-list-table"
        columns={columns(t, openDeleteModal, hasAuthority)}
        data={clients ?? []}
        filters={[]}
        total={total ?? 0}
        loading={status === "loading"}
        searchBy="search"
        noResults={
          <EmptyState
            title={t("clients.empty.title")}
            icon={<ClientsIcon />}
            subtitle={
              hasAuthority(["AUTH_EDIT_CLIENT_GROUP"])
                ? t("clients.empty.subtitle")
                : undefined
            }
          >
            {hasAuthority(["AUTH_EDIT_CLIENT_GROUP"]) ? (
              <span
                onClick={onOpen}
                className={cx(
                  "BodyBody-1Primary-mainRegular",
                  styles.createNew,
                )}
              >
                {t("clients.create_button")}
              </span>
            ) : null}
          </EmptyState>
        }
        actionButton={
          <WithAuthority allowed={["AUTH_EDIT_CLIENT_GROUP"]}>
            <Button variant="contained" color="primary" onClick={onOpen}>
              {t("clients.button.add")}
            </Button>
          </WithAuthority>
        }
      />
      <AddClientModal
        clientId={id}
        open={open}
        onClose={onClose}
        existingClientIds={existingClientIds}
      />
      <SnackBar
        open={Boolean(showSuccess)}
        severity="success"
        onClose={() => setShowSuccess(undefined)}
        message={showSuccess ?? ""}
      />
      <ConfirmationModal
        onCancel={() => setModalInfo(undefined)}
        open={Boolean(modalInfo)}
        {...modalInfo}
      />
    </>
  )
}

export default ClientgroupClients
